import React, { useEffect } from 'react';
import './marketing.css';
import {
  IconShield,
  IconStar,
  IconZap,
  IconCheck,
  IconScale,
  IconLightbulb,
  IconCalculator,
} from '../components/Icons';

const VALUES = [
  {
    icon: <IconLightbulb size={20} />,
    title: 'Clarity over complexity',
    text:
      'Risk should be obvious before a trade, not after. We translate prop-firm math into a single, readable answer.',
  },
  {
    icon: <IconScale size={20} />,
    title: 'Provider-agnostic',
    text:
      'Prop-firm rules vary, and we model them accurately without taking sides. If a firm updates a rule, we update RiskLo.',
  },
  {
    icon: <IconShield size={20} />,
    title: 'Security by default',
    text:
      'Google-verified OAuth, read-only Sheets, encrypted in transit. We never store broker credentials or take live trades.',
  },
  {
    icon: <IconZap size={20} />,
    title: 'Built for speed',
    text:
      'A risk assessment should take seconds, not a 90-minute spreadsheet session. We design for traders who size up on a schedule.',
  },
];

const PILLARS = [
  {
    icon: <IconCalculator size={22} />,
    title: 'Instant risk math',
    text:
      'A unified blowout and consistency engine replaces the spreadsheets, ad-hoc formulas, and Discord napkin math.',
  },
  {
    icon: <IconStar size={22} />,
    title: 'Real account context',
    text:
      'NQ ↔ MNQ scaling, account presets, start-of-day P&L, and windfall logic — all built in so the math matches reality.',
  },
  {
    icon: <IconShield size={22} />,
    title: 'Designed for prop traders',
    text:
      'Consistency rules from 20% to 60% are first-class. Every output respects the way prop firms actually settle.',
  },
];

const PERSONAS = [
  {
    title: 'Funded prop traders',
    text:
      'Manage multiple accounts, run consistency-aware sizing, and avoid clawbacks before a payout window.',
  },
  {
    title: 'Systematic futures traders',
    text:
      'Run your brand\'s strategies through portfolio simulation, strategy analysis, and benchmarks before allocating capital.',
  },
  {
    title: 'Coaches & mentors',
    text:
      'Show students the math behind position sizing in plain English instead of a wall of spreadsheets.',
  },
];

function AboutPage({ onNavigate }) {
  const go = (page) => () => onNavigate && onNavigate(page);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const items = document.querySelectorAll('.marketing-reveal');
    if (!items.length) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
    );

    items.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="marketing-root marketing-about-in-app-shell">
      <section className="marketing-page-header">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-eyebrow marketing-reveal">About RiskLo</div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            We built the risk tool we<br />wished existed when we started.
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '1rem auto 0' }}>
            RiskLo was born from a simple frustration: understanding trading risk
            shouldn't take an hour and a spreadsheet. It should take five seconds and
            tell you whether to take the trade.
          </p>
        </div>
      </section>

      <section className="marketing-section tight" style={{ paddingTop: 0 }}>
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-feature-grid marketing-reveal" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            {VALUES.map((v) => (
              <div key={v.title} className="marketing-feature-card">
                <div className="marketing-feature-icon">{v.icon}</div>
                <h3 className="marketing-feature-title">{v.title}</h3>
                <p className="marketing-feature-text">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section tight">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-cta marketing-reveal" style={{ textAlign: 'left', padding: 'clamp(1.5rem, 4vw, 2.25rem)' }}>
            <div className="marketing-cta-inner" style={{ maxWidth: '100%' }}>
              <div className="marketing-eyebrow">Product updates</div>
              <h2 className="marketing-title" style={{ fontSize: 'clamp(1.35rem, 3vw, 1.85rem)', margin: '0.65rem 0 0.5rem' }}>
                RiskLo Update Log
              </h2>
              <p style={{ margin: 0, color: 'var(--m-text-muted)', lineHeight: 1.6, maxWidth: '42rem' }}>
                Changelog for RiskLo releases—integrations, risk tools, results features, and major UI updates—with
                the newest entry first.
              </p>
              <div className="marketing-hero-cta-row" style={{ marginTop: '1.25rem', justifyContent: 'flex-start' }}>
                <button type="button" className="marketing-btn" onClick={go('updates')}>
                  View Update Log
                </button>
                <button type="button" className="marketing-btn-ghost" onClick={go('features')}>
                  Explore features
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">The problem</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              Risk math is harder than it looks.
            </h2>
          </div>
          <div className="marketing-reveal" style={{ color: 'var(--m-text-muted)', fontSize: '1.05rem', lineHeight: 1.7 }}>
            <p>
              Traders are constantly making decisions that can make or break an account.
              But knowing whether you're taking <em>too much</em> risk is hard. Drawdowns
              are intraday, strategies report end-of-day, contracts scale differently per
              instrument, and prop-firm consistency rules change between providers.
            </p>
            <p style={{ marginTop: '1rem' }}>
              By the time you've pulled historical worst losses, scaled them to your
              contract count, factored in start-of-day profit and a 50% consistency rule —
              the trade is already gone.
            </p>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">The solution</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              One dashboard. Live answers.
            </h2>
          </div>

          <div className="marketing-feature-grid">
            {PILLARS.map((p, i) => (
              <div key={p.title} className="marketing-feature-card marketing-reveal" data-delay={i + 1}>
                <div className="marketing-feature-icon">{p.icon}</div>
                <h3 className="marketing-feature-title">{p.title}</h3>
                <p className="marketing-feature-text">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">Who it's for</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              Traders who want their math automated.
            </h2>
          </div>
          <div className="marketing-persona-grid">
            {PERSONAS.map((p, i) => (
              <div key={p.title} className="marketing-persona marketing-reveal" data-delay={i + 1}>
                <h4>{p.title}</h4>
                <p>{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">What we promise</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              We respect your time and your account.
            </h2>
          </div>

          <ul className="marketing-split-list marketing-reveal" style={{ fontSize: '1rem', maxWidth: 700, margin: '0 auto' }}>
            <li><IconCheck size={16} /> No live trading, no broker credentials stored, no surprise actions on your account.</li>
            <li><IconCheck size={16} /> Read-only access where data is connected, and only the minimum scopes Google supports.</li>
            <li><IconCheck size={16} /> Provider-agnostic math — we don't get a kickback if a specific firm gets recommended.</li>
            <li><IconCheck size={16} /> Honest disclaimers when historical data underestimates intraday drawdown risk.</li>
            <li><IconCheck size={16} /> Transparent calculations — every number is explained on the "How we calculate" page.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
